import { NgZone } from '@angular/core';

import { LogLevel } from '../constants/log.constants';

import { Logger } from './logger';
import { ServiceWorkerActivation } from './service-worker-activation';

export class SyncMessenger {

  private logger: Logger;
  private serviceWorkerActivation: ServiceWorkerActivation;
  private listenersByType: Map<string, (message: any) => void>;
  private messageListener: (event: MessageEvent) => void;

  constructor(
    private ngZone: NgZone,
    private knownUnhandledMessageTypes: string[],
    logLevel: LogLevel) {

    this.knownUnhandledMessageTypes ??= [];
    this.logger = new Logger(this.constructor.name, logLevel);
    this.serviceWorkerActivation = new ServiceWorkerActivation(logLevel);
    this.listenersByType = new Map();

    this.messageListener = (event: MessageEvent) => this.onMessage(event);
    navigator.serviceWorker.addEventListener('message', this.messageListener);
  }

  destroy(): void {

    for (const type of this.listenersByType.keys()) {
      this.removeListener(type);
    }

    navigator.serviceWorker.removeEventListener('message', this.messageListener);
  }

  static async postMessageNoWait<TMessage = any>(serviceWorker: ServiceWorker, type: string, message?: TMessage): Promise<void> {
    serviceWorker.postMessage({ type, message });
  }

  async postMessage<TMessage = any>(type: string, message?: TMessage): Promise<void> {

    const logPrefix = 'postMessage';

    const serviceWorker = await this.serviceWorkerActivation.wait();

    this.logger.log(`${logPrefix}: Posting message to service worker`, { type, message });
    await SyncMessenger.postMessageNoWait<TMessage>(serviceWorker, type, message);
  }

  addListener<TMessage = any>(type: string, listener: (message?: TMessage) => void): void {

    const logPrefix = 'addListener';

    if (this.listenersByType.has(type)) {
      this.logger.warn(`${logPrefix}: Received request to add listener for type '${type}', but a listener already exists: listener is being replaced.`);
    }
    else {
      this.logger.info(`${logPrefix}: Adding listener for type '${type}'`);
    }

    this.listenersByType.set(type, listener);
  }

  removeListener(type: string): void {

    const logPrefix = 'removeListener';

    if (!this.listenersByType.delete(type)) {
      this.logger.warn(`${logPrefix}: Received request to remove listener for type '${type}', but the listener did not exist: request is being ignored.`);
    }
    else {
      this.logger.info(`${logPrefix}: Removed listener for type '${type}'`);
    }
  }

  private onMessage(event: MessageEvent): void {

    const logPrefix = 'onMessage';

    const { type, message } = event.data;
    const listener = this.listenersByType.get(type);

    if (!listener) {
      const logFunction = this.knownUnhandledMessageTypes.includes(type) ? this.logger.info : this.logger.warn;
      logFunction(`${logPrefix}: Received message of type '${type}', but no listener has been found: message is being ignored.`);
      return;
    }

    this.logger.info(`${logPrefix}: Received message`, { type, message });

    // Messages initiated by the service worker are outside the Angular "zone",
    // therefore they need to be brought in the "zone",
    // otherwise Angular change detection (and other features) won't work.
    this.ngZone.run(() => listener(message)); 
  }
}
